/**
 * Once per page load: boot the platform (device id, visit, `app_opened`, delivery), read
 * a share link's params (stripping them from the address bar) and track the share open.
 */
import type { DateKey } from '@arrow/game-engine';
import type { Level } from '@arrow/puzzle-schema';

import { initPlatform, type PlatformInfo, readShareParams, type ShareParams, track } from '../platform/index.ts';
import { noteLaunch } from '../pwa/install.ts';
import { readPlatform } from '../pwa/platform.ts';
import { parseRoute, seedHistory } from './router.tsx';

export interface Boot {
	info: PlatformInfo;
	share: ShareParams;
	/** The path the app opened on (before any navigation). */
	path: string;
}

let booted: Boot | null = null;

export function boot(todayKey: DateKey): Boot {
	if (booted) return booted;
	const path = typeof window === 'undefined' ? '/' : window.location.pathname;
	const display = readPlatform().standalone ? 'standalone' : 'browser';
	const info = initPlatform({ route: path, todayKey, display });
	// From the home screen: `opened_standalone` (and `installed`, the first time).
	if (display === 'standalone') noteLaunch();
	const share = readShareParams();
	const route = parseRoute(path);
	// `readShareParams` strips `l` with the share marks; a game route keeps its level in
	// the address, so a reload opens the same level (only `s` marked it as a share open).
	if (share.level && (route.name === 'daily' || route.name === 'practice')) keepLevel(share.level);
	// A cold game, practice or challenge link gets Today underneath it (app/router.tsx).
	seedHistory();
	if (share.fromShare) {
		track('shared_link_opened', {
			...(route.name === 'daily' ? { number: route.number } : {}),
			...(share.level ? { level: share.level } : {}),
		});
	}
	booted = { info, share, path };
	return booted;
}

function keepLevel(level: Level) {
	try {
		const here = new URL(window.location.href);
		here.searchParams.set('l', level);
		window.history.replaceState(window.history.state, '', `${here.pathname}?${here.searchParams.toString()}${here.hash}`);
	} catch {
		// The route still opens; at the remembered level.
	}
}

/** Tests: boot again. */
export function resetBoot(): void {
	booted = null;
}
